import { useState, useRef, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import {
  APPLICATION_STEPS,
  MerchantApplicationData,
  getApplicationSteps,
  ApplicationStatus,
  ApplicationStep,
  PersonalInfo,
  CorporationInfo,
  BankDetails,
  IntegrationLogEntry,
} from '../types';
import { db } from '@/lib/firebase';
import { doc, setDoc, getDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { useQuery } from '@tanstack/react-query';
import { getCompany } from '@/lib/api';
import { cleanAndFormatPhone } from '@/utils/formatters';
import { useMerchantApplicationSettings } from '../hooks/useMerchantApplicationSettings';
import { ApplicationSource } from '../types/fieldSettings';
import { payarcService } from '@/services/payarcService';

// Import step components
import { PersonalInformationStep } from './PersonalInformationStep';
import { CorporationInfoStep } from './CorporationInfoStep';
import { ProcessingHistoryStep } from './ProcessingHistoryStep';
import { BeneficialOwnerStep } from './BeneficialOwnerStep';
import { BankDetailsStep } from './BankDetailsStep';
import { DocumentationStep } from './DocumentationStep';

export interface ApplicationCompletionResult {
  status: ApplicationStatus | 'awaiting_signature';
  applicationId: string;
  merchantCode?: string;
  errorMessage?: string;
}

interface MerchantApplicationFormProps {
  onComplete?: (result?: ApplicationCompletionResult) => void;
  applicationSource: ApplicationSource;  // Required - parent must provide source ID
}

export function MerchantApplicationForm({ onComplete, applicationSource }: MerchantApplicationFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(() => {
    // Initialize from localStorage to persist across tab switches
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('merchantAppCurrentStep');
      return saved ? parseInt(saved, 10) : 1;
    }
    return 1;
  });
  const [formData, setFormData] = useState<Partial<MerchantApplicationData>>({
    applicationSource: applicationSource as ApplicationSource,
  });
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  // Persist currentStep to localStorage to survive tab switches
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('merchantAppCurrentStep', currentStep.toString());
    }
  }, [currentStep]);

  // Load conditional field settings based on application source and selected SIC code
  const {
    loading: settingsLoading,
    settings,
    sourceSettings,
    classificationSettings,
    currentClassification,
    shouldShowField,
    isFieldRequired,
    getFieldDefaultValue,
    getRequiredDocuments,
    getEnabledDocuments,
    shouldShowStep,
    getSectionConfig,
    getSectionTitle,
    getSectionSubtitle,
    getOrderedSections,
  } = useMerchantApplicationSettings(applicationSource, formData.corporationInfo?.sicCode, formData.corporationInfo?.sicDescription);

 
  // Helper function to sort steps based on sectionOrder configuration
  const sortStepsByOrder = (a: ApplicationStep, b: ApplicationStep): number => {
    const sectionOrder = classificationSettings?.sectionOrder;

    // If no custom order configured, maintain original order
    if (!sectionOrder || sectionOrder.length === 0) {
      return 0;
    }

    // Map step titles to section names (same as in shouldShowStep)
    const stepToSectionMap: Record<string, string> = {
      'Personal Information': 'Personal Information',
      'Corporation Info': 'Corporation Information',
      'Processing History': 'Processing History',
      'Beneficial Owners': 'Beneficial Owners',
      'Bank Details': 'Bank Details',
      'Documentation': 'Documentation',
    };

    const sectionA = stepToSectionMap[a.title];
    const sectionB = stepToSectionMap[b.title];

    // Get positions in sectionOrder array
    const indexA = sectionA ? sectionOrder.indexOf(sectionA) : -1;
    const indexB = sectionB ? sectionOrder.indexOf(sectionB) : -1;

    // If both sections are in the order, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one is in the order, it comes first
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither is in the order, maintain original order
    return 0;
  };

  // Get the appropriate steps based on application type and section visibility
  const allSteps = getApplicationSteps(formData.applicationType);
  const currentSteps = allSteps
    .filter(step => shouldShowStep(step.title))
    .sort(sortStepsByOrder);

  const currentStepIndex = currentSteps.findIndex(step => step.id === currentStep);
  const progress = ((currentStepIndex + 1) / currentSteps.length) * 100;

  const { data: company } = useQuery({
    queryKey: ['company', user?.companyId],
    queryFn: () => getCompany(user?.companyId as string),
    enabled: !!user?.companyId
  });

  // Load existing application data or pre-fill from company and user profile
  useEffect(() => {
    const loadApplicationData = async () => {
      // Skip if data already loaded (prevents reset on tab switch/remount)
      if (isDataLoaded) return;
      if (!user || !company) return;

      try {
        setIsLoading(true);
        console.log("user.uid", user.uid)
        // Fetch user document to get all fields including firstName, lastName, and address
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};
        
        console.log('User data from Firebase:', userData);
        console.log('User displayName:', user.displayName);
        
        // Check if there's an existing application
        const appDoc = await getDoc(
          doc(db, 'merchantApplications', `${company.id}_application`)
        );

        if (appDoc.exists()) {
          const appData = appDoc.data();
          setApplicationId(appDoc.id);
          setFormData(appData.data || {});
          // Only restore step from Firestore if localStorage doesn't have a saved step
          const savedStep = typeof window !== 'undefined' ? localStorage.getItem('merchantAppCurrentStep') : null;
          if (!savedStep && appData.data?.currentStep) {
            console.log("appData.data.currentStep", appData.data.currentStep)
            setCurrentStep(appData.data.currentStep);
          }
          setCompletedSteps(appData.data?.completedSteps || []);
        } else {
          // Pre-fill data from user profile and company settings
          const displayNameParts = user.displayName?.split(' ') || [];
          
          // Use existing firstName/lastName if available, otherwise parse from displayName
          let firstName = userData.firstName || '';
          let lastName = userData.lastName || '';
          
          // If we don't have firstName/lastName but have displayName, parse it
          if ((!firstName || !lastName) && displayNameParts.length > 0) {
            firstName = firstName || displayNameParts[0] || '';
            lastName = lastName || displayNameParts.slice(1).join(' ') || '';
          }
          
          const companyAddress = company.address || {};
          const derivedPostalCode =
            companyAddress.zipCode ||
            companyAddress.postalCode ||
            companyAddress.zip ||
            companyAddress.postCode ||
            companyAddress.postal ||
            '';

          const initialData: Partial<MerchantApplicationData> = {
            personalInfo: {
              firstName,
              lastName,
              email: user.email || '',
              phone: cleanAndFormatPhone(userData.phone) || cleanAndFormatPhone(company.phone),
              // personalAddress removed - no longer collected in personal info step
            },
            applicationType: 'corporation',
            corporationInfo: {
              businessName: company.name || '',
              corporationType: 'llc',
              businessStartDate: '',
              ein: '',
              dba: '',
              website: '',
              businessDescription: '',
              sicCode: '',
              sicDescription: '',
              customerServiceEmail: user.email || '',
              customerServicePhone: cleanAndFormatPhone(company.phone),
              businessAddress: company.address ? {
                street: companyAddress.street || '',
                city: companyAddress.city || '',
                state: companyAddress.state || '',
                zipCode: derivedPostalCode,
                fullAddress: [
                  companyAddress.street,
                  [companyAddress.city, companyAddress.state].filter(Boolean).join(', '),
                  derivedPostalCode,
                ]
                  .filter(part => !!part && String(part).trim().length > 0)
                  .join(' ')
                  .trim(),
              } : {
                street: '',
                city: '',
                state: '',
                zipCode: '',
                fullAddress: '',
              },
            },
            pricing: {
              online: { processingFee: 3.5, transactionFee: 30 },
              offline: { processingFee: 3.5, transactionFee: 30 },
              chargebackFee: 35,
              monthlyMinimum: 0,
            },
            currentStep: 1,
            completedSteps: [],
          };
          
          console.log('Setting initial form data:', initialData);
          
          setFormData(initialData);
          setApplicationId(`${company.id}_application`);
        }
      } catch (error) {
        console.error('Error loading application data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load application data',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
        setIsDataLoaded(true);
      }
    };

    loadApplicationData();
  }, [user, company, toast, isDataLoaded]);

  const hydrateWithDefaults = useCallback((data?: Partial<MerchantApplicationData>) => {
    if (!data) return data;

    const hydrated: Partial<MerchantApplicationData> = { ...data };

    const applyDefaults = (
      paths: string[],
      resolver: (path: string, defaultValue: any) => void,
    ) => {
      paths.forEach((path) => {
        if (!shouldShowField(path)) {
          const defVal = getFieldDefaultValue?.(path);
          if (defVal !== undefined) {
            resolver(path, defVal);
          }
        }
      });
    };

    let personalInfo = hydrated.personalInfo ? { ...hydrated.personalInfo } : undefined;
    applyDefaults(
      ['personalInfo.firstName', 'personalInfo.lastName', 'personalInfo.email', 'personalInfo.phone'],
      (_, defaultValue) => {
        if (!personalInfo) personalInfo = { ...(hydrated.personalInfo || {}) } as PersonalInfo;
        const key = _.split('.').pop() as keyof PersonalInfo;
        personalInfo![key] = defaultValue;
      }
    );
    if (personalInfo) hydrated.personalInfo = personalInfo;

    let corporationInfo = hydrated.corporationInfo ? { ...hydrated.corporationInfo } : undefined;
    applyDefaults(
      [
        'corporationInfo.businessName',
        'corporationInfo.corporationType',
        'corporationInfo.businessStartDate',
        'corporationInfo.ein',
        'corporationInfo.dba',
        'corporationInfo.website',
        'corporationInfo.businessDescription',
        'corporationInfo.sicCode',
        'corporationInfo.sicDescription',
        'corporationInfo.customerServiceEmail',
        'corporationInfo.customerServicePhone',
      ],
      (path, defaultValue) => {
        if (!corporationInfo) corporationInfo = { ...(hydrated.corporationInfo || {}) } as CorporationInfo;
        const key = path.split('.').pop() as keyof CorporationInfo;
        (corporationInfo as any)[key] = defaultValue;
      }
    );

    let businessAddress = corporationInfo?.businessAddress
      ? { ...corporationInfo.businessAddress }
      : undefined;
    applyDefaults(
      [
        'corporationInfo.businessAddress.street',
        'corporationInfo.businessAddress.city',
        'corporationInfo.businessAddress.state',
        'corporationInfo.businessAddress.zipCode',
        'corporationInfo.businessAddress.fullAddress',
      ],
      (path, defaultValue) => {
        if (!corporationInfo) corporationInfo = { ...(hydrated.corporationInfo || {}) } as CorporationInfo;
        if (!businessAddress) {
          businessAddress = { ...(corporationInfo?.businessAddress || {}) } as any;
        }
        const key = path.split('.').pop() as keyof CorporationInfo['businessAddress'];
        if (!businessAddress) businessAddress = {} as any;
        (businessAddress as any)[key] = defaultValue;
      }
    );
    if (businessAddress) {
      corporationInfo = corporationInfo || ({} as CorporationInfo);
      corporationInfo.businessAddress = businessAddress as any;
    }
    if (corporationInfo) hydrated.corporationInfo = corporationInfo;

    let processingHistory = hydrated.processingHistory
      ? { ...hydrated.processingHistory }
      : undefined;
    applyDefaults(
      [
        'processingHistory.isCurrentlyProcessing',
        'processingHistory.monthlyVolume',
        'processingHistory.averageTicket',
        'processingHistory.highTicket',
        'processingHistory.salesDeliveryDays',
        'processingHistory.whenIsCustomerCharged',
        'processingHistory.orderTurnaroundTime',
        'processingHistory.advancedDeposits',
        'processingHistory.depositPct',
        'processingHistory.currentProcessor',
        'processingHistory.cardPresentPercentage',
        'processingHistory.ecommercePercentage',
        'processingHistory.acceptOfflineCards',
        'processingHistory.retailPercentage',
        'processingHistory.onlinePercentage',
        'processingHistory.b2bPercentage',
        'processingHistory.b2cPercentage',
        'processingHistory.bankruptcy',
        'processingHistory.hasBeenTerminated',
        'processingHistory.terminationExplanation',
      ],
      (path, defaultValue) => {
        if (!processingHistory) processingHistory = { ...(hydrated.processingHistory || {}) } as any;
        const key = path.split('.').pop() as string;
        (processingHistory as any)[key] = defaultValue;
      }
    );
    if (processingHistory) hydrated.processingHistory = processingHistory as any;

    let bankDetails = hydrated.bankDetails ? { ...hydrated.bankDetails } : undefined;
    applyDefaults(
      ['bankDetails.bankName', 'bankDetails.routingNumber', 'bankDetails.accountNumber', 'bankDetails.accountType'],
      (path, defaultValue) => {
        if (!bankDetails) bankDetails = { ...(hydrated.bankDetails || {}) } as BankDetails;
        const key = path.split('.').pop() as keyof BankDetails;
        bankDetails![key] = defaultValue;
      }
    );
    if (!bankDetails && (hydrated as any).bankName) {
      bankDetails = {
        bankName: (hydrated as any).bankName,
        routingNumber: (hydrated as any).routingNumber,
        accountNumber: (hydrated as any).accountNumber,
      };
    }
    if (bankDetails) hydrated.bankDetails = bankDetails;

    let pricing = hydrated.pricing ? { ...hydrated.pricing } : undefined;
    if (!pricing) {
      pricing = {} as any;
    }
    pricing.online = {
      processingFee: pricing.online?.processingFee ?? 3.5,
      transactionFee: pricing.online?.transactionFee ?? 30,
    };
    pricing.offline = {
      processingFee: pricing.offline?.processingFee ?? 3.5,
      transactionFee: pricing.offline?.transactionFee ?? 30,
    };
    if (pricing.chargebackFee === undefined || pricing.chargebackFee === null) {
      pricing.chargebackFee = 35;
    }
    if (pricing.monthlyMinimum === undefined || pricing.monthlyMinimum === null) {
      pricing.monthlyMinimum = pricing.monthlyMinimum ?? 0;
    }
    hydrated.pricing = pricing as any;

    return hydrated;
  }, [shouldShowField, getFieldDefaultValue]);

  const saveApplicationData = async (stepData: any, stepNumber: number) => {
    if (!user || !company || !applicationId) return;

    const updatedData = {
      ...formData,
      ...stepData,
      currentStep: stepNumber,
      completedSteps: [...new Set([...completedSteps, stepNumber])],
    };

    try {
      // Get the pipeline status from the source settings
      const pipelineStatus = sourceSettings?.defaultPipelineStatus || 'lead';
      
  
      
      await setDoc(
        doc(db, 'merchantApplications', applicationId),
        {
          companyId: company.id,
          userId: user.uid,
          status: 'in_progress',
          pipelineStatus,
          applicationSource: applicationSource, // Track the source
          data: updatedData,
          updatedAt: new Date(),
          createdAt: new Date(), // Will only set on first save
        },
        { merge: true }
      );

      // Update company document to track application status
      await setDoc(
        doc(db, 'companies', company.id),
        {
          merchantApplication: {
            status: 'in_progress',
            applicationId: applicationId,
            currentStep: stepNumber,
            updatedAt: new Date(),
          },
        },
        { merge: true }
      );

      setFormData(updatedData);
      setCompletedSteps([...new Set([...completedSteps, stepNumber])]);
    } catch (error) {
      console.error('Error saving application data:', error);
      console.error('Step data that failed:', stepData);
      console.error('Updated data that failed:', updatedData);
      throw error;
    }
  };

  const handleStepSubmit = async (stepData: any) => {
    try {
      await saveApplicationData(stepData, currentStep);
      
      const updatedFormData = { ...formData, ...stepData };
      
      // If this is step 1 (personal info), save the data to the user profile
      if (currentStep === 1 && stepData.personalInfo) {
        // Create displayName from firstName and lastName
        const displayName = `${stepData.personalInfo.firstName} ${stepData.personalInfo.lastName}`.trim();
        
        const userProfileUpdate: any = {
          firstName: stepData.personalInfo.firstName,
          lastName: stepData.personalInfo.lastName,
          displayName: displayName, // Keep displayName in sync
          phone: stepData.personalInfo.phone,
          // address removed - no longer collected in personal info step
          updatedAt: new Date(),
        };
        
        // Update the user profile with the personal information
        await setDoc(
          doc(db, 'users', user.uid),
          userProfileUpdate,
          { merge: true }
        );
      }
      
      // Handle conditional navigation based on application type
      if (currentStep === 1 && updatedFormData.applicationType) {
        // After personal info + application type selection, go to the appropriate next step
        // Get filtered steps based on visibility settings
        const allSteps = getApplicationSteps(updatedFormData.applicationType);
        const visibleSteps = allSteps.filter(step => shouldShowStep(step.title)).sort(sortStepsByOrder);

        // Find the next visible step after Personal Information
        const personalInfoIndex = visibleSteps.findIndex(step => step.title === 'Personal Information');
        if (personalInfoIndex < visibleSteps.length - 1) {
          const nextStep = visibleSteps[personalInfoIndex + 1];
          setCurrentStep(nextStep.id);
        }
      } else {
        // Get the visible steps based on application type and section visibility
        const allSteps = getApplicationSteps(updatedFormData.applicationType);
        const visibleSteps = allSteps.filter(step => shouldShowStep(step.title)).sort(sortStepsByOrder);
        const currentStepIndex = visibleSteps.findIndex(step => step.id === currentStep);

        // Debug logging for SIC code and classification
        console.log('📋 Selected SIC Code:', updatedFormData.corporationInfo?.sicCode);
        console.log('🏷️ Current Classification:', currentClassification);
        console.log('📊 Current step index:', currentStepIndex);
        console.log('📝 Visible steps:', visibleSteps.map(s => s.title));
        console.log('🔢 Current step ID:', currentStep);

        if (currentStepIndex < visibleSteps.length - 1) {
          const nextStep = visibleSteps[currentStepIndex + 1];
          console.log('🚀 Navigating to next step:', nextStep.title, 'ID:', nextStep.id);
          setCurrentStep(nextStep.id);
        } else {
          // Final step completed - submit application
          await submitApplication();
        }
      }
    } catch (error) {
      console.error('Error in handleStepSubmit:', error);
      console.error('Step data in handleStepSubmit:', stepData);
      toast({
        title: 'Error',
        description: `Failed to save progress: ${error.message || 'Unknown error'}`,
        variant: 'destructive',
      });
    }
  };

  const submitApplication = async () => {
    if (!applicationId || !company) return;

    const pipelineStatus = sourceSettings?.defaultPipelineStatus || 'lead';
    const submissionTimestamp = new Date();

    const applicationRef = doc(db, 'merchantApplications', applicationId);
    const latestSnapshot = await getDoc(applicationRef);
    const storedData = (latestSnapshot.exists() ? latestSnapshot.data()?.data : {}) as Partial<MerchantApplicationData>;

    const submissionSource = {
      ...storedData,
      submittedAt: submissionTimestamp,
    } as Partial<MerchantApplicationData>;

    const submissionPayload = (hydrateWithDefaults(submissionSource) ?? submissionSource) as MerchantApplicationData;

    let hydratedData: Partial<MerchantApplicationData> = { ...submissionPayload };

    let completionStatus: ApplicationStatus | 'awaiting_signature' = 'submitted';
    let computedPipelineStatus = pipelineStatus;
    let merchantCode: string | undefined;
    let errorMessage: string | undefined;

    const recordIntegrationLog = async (
      entry: Omit<IntegrationLogEntry, 'timestamp'> & { timestamp?: string }
    ) => {
      try {
        const logEntry: IntegrationLogEntry = {
          ...entry,
          timestamp: entry.timestamp ?? new Date().toISOString(),
        };

        await setDoc(
          applicationRef,
          {
            'data.integrationLogs': arrayUnion(logEntry),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (logError) {
        console.warn('Unable to record integration log:', logError);
      }
    };

    try {
      // Load banking partner from merchant application settings
      let sourceBankingPartnerId: string | undefined = undefined;
      let sourceBankIsPayArc = false;

      // Get banking partner ID from source settings (loaded from merchantApplicationSettings)
      try {
        sourceBankingPartnerId = sourceSettings?.bankingPartnerId;

        if (sourceBankingPartnerId) {
          console.log('⚙️ Application source banking partner ID:', sourceBankingPartnerId);

          // Verify it's a PayArc partner
          const partnerDoc = await getDoc(doc(db, 'bankingPartners', sourceBankingPartnerId));
          if (partnerDoc.exists()) {
            const partnerName = String(partnerDoc.data()?.name || '');
            sourceBankIsPayArc = partnerName.trim().toLowerCase().includes('payarc');
            console.log('⚙️ Banking partner name:', partnerName, 'Is PayArc:', sourceBankIsPayArc);
          }
        } else {
          console.warn('⚠️ No banking partner configured for application source:', applicationSource);
        }
      } catch (sourceError) {
        console.warn('Unable to load banking partner from merchant settings:', sourceError);
      }

      if (sourceBankingPartnerId && sourceBankIsPayArc) {
        try {
          const applicationSnap = await getDoc(applicationRef);
          const assignedBanks: string[] = applicationSnap.exists()
            ? applicationSnap.data()?.assignedBanks || []
            : [];

          if (!assignedBanks.includes(sourceBankingPartnerId)) {
            await setDoc(
              applicationRef,
              { assignedBanks: [...assignedBanks, sourceBankingPartnerId] },
              { merge: true }
            );
          }
        } catch (assignError) {
          console.warn('Unable to assign banking partner to application:', assignError);
        }

        // Check if application is high-risk - if so, block automatic PayArc submission
        if (currentClassification === 'high-risk') {
          // HIGH-RISK: Do NOT send to PayArc automatically - requires admin review
          completionStatus = 'submitted';
          computedPipelineStatus = 'lead'; // Keep in lead status for admin attention

          await recordIntegrationLog({
            source: 'payarc-lead',
            status: 'success',
            message: 'High-risk application submitted. Awaiting admin review before PayArc submission.',
            actor: 'application',
            details: JSON.stringify({
              sicCode: submissionPayload.corporationInfo?.sicCode,
              classification: currentClassification,
              reason: 'Traditional/High-risk SIC code selected - requires manual admin review'
            }, null, 2),
          });

          toast({
            title: 'Application Submitted for Review',
            description: 'Your application has been submitted. Our team will review it and contact you within 24 hours.',
          });
        } else {
          // LOW-RISK: Send to PayArc automatically (existing flow)
          console.log('🏦 Submitting to PayArc with banking partner ID:', sourceBankingPartnerId);
          const leadResponse = await payarcService.createLead(submissionPayload, sourceBankingPartnerId);

          if (leadResponse.success && leadResponse.MerchantCode) {
            merchantCode = leadResponse.MerchantCode;
            completionStatus = 'awaiting_signature';
            computedPipelineStatus = 'documents';
            hydratedData = {
              ...submissionPayload,
              payArcMerchantCode: leadResponse.MerchantCode,
              payArcSubmittedAt: submissionTimestamp,
            };

            await recordIntegrationLog({
              source: 'payarc-lead',
              status: 'success',
              message: `Application sent to PayArc. Merchant Code: ${leadResponse.MerchantCode}`,
              actor: 'application',
              details: leadResponse?.data ? JSON.stringify(leadResponse.data, null, 2) : undefined,
            });

            toast({
              title: 'Application Sent to PayArc',
              description: 'We generated your PayArc application. Review and sign the terms to finish.',
            });
          } else {
            errorMessage = leadResponse.message || 'Failed to send application to PayArc';
            // Move application to failed_submission pipeline status
            completionStatus = 'submitted';
            computedPipelineStatus = 'failed_submission';

            await recordIntegrationLog({
              source: 'payarc-lead',
              status: 'error',
              message: errorMessage,
              actor: 'application',
              details: JSON.stringify(leadResponse.errors ?? leadResponse, null, 2),
            });
            toast({
              title: 'PayArc Submission Failed',
              description: errorMessage,
              variant: 'destructive',
            });
          }
        }
      } else {
        toast({
          title: 'Application Submitted',
          description: 'Your merchant application has been submitted successfully!',
        });
      }
    } catch (error: any) {
      console.error('Error submitting application to PayArc:', error);
      errorMessage = error?.message || 'Failed to submit application. Please try again.';
      // Move application to failed_submission pipeline status on any error
      completionStatus = 'submitted';
      computedPipelineStatus = 'failed_submission';

      await recordIntegrationLog({
        source: 'payarc-lead',
        status: 'error',
        message: errorMessage,
        actor: 'application',
        details: error?.stack || JSON.stringify(error, null, 2),
      });
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }

    try {
      await setDoc(
        applicationRef,
        {
          status: completionStatus,
          pipelineStatus: computedPipelineStatus,
          data: hydratedData,
        },
        { merge: true }
      );

      await setDoc(
        doc(db, 'companies', company.id),
        {
          merchantApplication: {
            status: completionStatus,
            submittedAt: submissionTimestamp,
            applicationId,
            payArcMerchantCode: merchantCode ?? null,
          },
        },
        { merge: true }
      );

      setFormData(hydratedData);

      // Clear localStorage step on successful completion
      if (typeof window !== 'undefined') {
        localStorage.removeItem('merchantAppCurrentStep');
      }

      onComplete?.({
        status: completionStatus,
        applicationId,
        merchantCode,
        errorMessage,
      });
    } catch (error) {
      console.error('Error finalizing application submission:', error);
      toast({
        title: 'Error',
        description: 'Failed to finalize application submission.',
        variant: 'destructive',
      });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      // Get the visible steps based on application type and section visibility
      const allSteps = getApplicationSteps(formData.applicationType);
      const visibleSteps = allSteps.filter(step => shouldShowStep(step.title));
      const currentStepIndex = visibleSteps.findIndex(step => step.id === currentStep);
      
      if (currentStepIndex > 0) {
        const previousStep = visibleSteps[currentStepIndex - 1];
        setCurrentStep(previousStep.id);
      }
    }
  };

  const handleStepClick = (stepId: number) => {
    // Get the visible steps based on application type and section visibility
    const allSteps = getApplicationSteps(formData.applicationType);
    const visibleSteps = allSteps.filter(step => shouldShowStep(step.title));
    const step = visibleSteps.find(s => s.id === stepId);
    
    // Only allow navigation to steps that are visible and available
    if (step && (completedSteps.includes(stepId) || stepId <= completedSteps.length + 1)) {
      setCurrentStep(stepId);
    }
  };

  if (isLoading || settingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const renderStep = () => {
    // Find the current step from the filtered visible steps
    const step = currentSteps.find(s => s.id === currentStep);

    // If step is not in the visible steps, return null (prevents rendering hidden steps)
    if (!step) {
      console.warn(`Step ${currentStep} is not visible based on current classification, skipping render`);
      return null;
    }

    // Render based on step title (which respects visibility filtering)
    switch (step.title) {
      case 'Personal Information':
        return (
          <PersonalInformationStep
            onSubmit={handleStepSubmit}
            initialData={formData}
            shouldShowField={shouldShowField}
            isFieldRequired={isFieldRequired}
            getFieldDefaultValue={getFieldDefaultValue}
            sectionTitle={getSectionTitle('Personal Information')}
            sectionSubtitle={getSectionSubtitle('Personal Information')}
          />
        );
      case 'Corporation Info':
        return (
          <CorporationInfoStep
            onSubmit={handleStepSubmit}
            onPrevious={handlePrevious}
            initialData={formData}
            shouldShowField={shouldShowField}
            isFieldRequired={isFieldRequired}
            getFieldDefaultValue={getFieldDefaultValue}
            sectionTitle={getSectionTitle('Corporation Information')}
            sectionSubtitle={getSectionSubtitle('Corporation Information')}
            applicationSource={applicationSource}
            applicationType={formData.applicationType}
            onSicCodeChange={(sicCode, sicDescription) => {
              // Update formData immediately when SIC code changes
              // This triggers the hook to re-evaluate classification BEFORE form submission
              console.log('🔄 [SIC CHANGE] Updating formData with SIC code:', sicCode, 'description:', sicDescription);
              setFormData(prev => ({
                ...prev,
                corporationInfo: {
                  ...prev.corporationInfo,
                  sicCode,
                  sicDescription,
                },
              }));
            }}
          />
        );
      case 'Processing History':
        return (
          <ProcessingHistoryStep
            onSubmit={handleStepSubmit}
            onPrevious={handlePrevious}
            initialData={formData}
            shouldShowField={shouldShowField}
            isFieldRequired={isFieldRequired}
            getFieldDefaultValue={getFieldDefaultValue}
          />
        );
      case 'Beneficial Owners':
        return (
          <BeneficialOwnerStep
            onSubmit={handleStepSubmit}
            onPrevious={handlePrevious}
            initialData={formData}
            shouldShowField={shouldShowField}
            isFieldRequired={isFieldRequired}
            getFieldDefaultValue={getFieldDefaultValue}
          />
        );
      case 'Bank Details':
        return (
          <BankDetailsStep
            onSubmit={handleStepSubmit}
            onPrevious={handlePrevious}
            initialData={formData}
            shouldShowField={shouldShowField}
            isFieldRequired={isFieldRequired}
            getFieldDefaultValue={getFieldDefaultValue}
          />
        );
      case 'Documentation':
        return (
          <DocumentationStep
            onSubmit={handleStepSubmit}
            onPrevious={handlePrevious}
            initialData={formData}
            getEnabledDocuments={getEnabledDocuments}
            getRequiredDocuments={getRequiredDocuments}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Let's get payment enabled</h2>
          <span className="text-sm text-gray-600">
            Step {currentSteps.findIndex(s => s.id === currentStep) + 1} of {currentSteps.length}
          </span>
        </div>
        <Progress value={progress} className="w-full h-2" />
      </div>

      {/* Step Navigation */}
      <div className="flex justify-between items-center">
        {currentSteps.map((step, index) => {
          const isCompleted = completedSteps.includes(step.id);
          const isCurrent = currentStep === step.id;
          const isClickable = isCompleted || step.id <= completedSteps.length + 1;
          const displayNumber = index + 1; // Use index for consecutive numbering

          return (
            <button
              key={step.id}
              onClick={() => handleStepClick(step.id)}
              disabled={!isClickable}
              className={`flex items-center ${
                isClickable ? 'cursor-pointer' : 'cursor-not-allowed'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isCompleted
                    ? 'bg-green-600 text-white'
                    : isCurrent
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {isCompleted ? '✓' : displayNumber}
              </div>
              <span
                className={`ml-2 text-sm hidden md:inline ${
                  isCurrent ? 'font-semibold' : ''
                }`}
              >
                {step.title}
              </span>
            </button>
          );
        })}
      </div>

      {/* Step Content */}
      <Card>{renderStep()}</Card>
    </div>
  );
}
